import { useEffect, useState } from "react"
import { Button, Card, Divider, Flex, Form, Input, InputNumber, Modal, Radio, Segmented } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { Text } from "ui/Typography"
import CustomDrawer from "ui/Drawer"
import { useDevice } from "hooks/useDevice"
import { spacings } from "themes/variables"
import { AFFECTS, PLACEHOLDER_TICKET_TYPES, PRICE_DIRECTION, PRICE_TYPE, PRICE_TYPE_OPTIONS } from "../../../constants/pricingConstants"
import { findConflictingDayAdjustment } from "../../../helpers/adjustmentConflicts"
import styles from "./AddDayAdjustmentModal.module.css"

export const APPLIES_ON = { WEEKDAYS: "weekdays", WEEKEND: "weekend", CUSTOM: "custom" }

// Lowercase 3-letter values to match the backend contract (custom_days: ["mon","fri"]);
// displayed capitalized via CSS (.chip text-transform).
const WEEKDAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"]

const AddDayAdjustmentModal = ({ open, editingItem, ticketTypes, existingAdjustments = [], onCancel, onSave }) => {
  const { t } = useTranslation()
  const { isMobile } = useDevice()
  const [form] = Form.useForm()
  const [appliesOn, setAppliesOn] = useState(APPLIES_ON.WEEKEND)
  const [customDays, setCustomDays] = useState([])
  const [affects, setAffects] = useState(AFFECTS.ALL)
  const [selectedTickets, setSelectedTickets] = useState([])
  const [direction, setDirection] = useState(PRICE_DIRECTION.INCREASE)
  const [priceType, setPriceType] = useState(PRICE_TYPE.PERCENT)
  const [isTicketSelectionMissing, setIsTicketSelectionMissing] = useState(false)
  // Name of the adjustment whose days clash with this one, shown inline under the picker.
  const [conflictingName, setConflictingName] = useState(null)

  useEffect(() => {
    if (open && editingItem) {
      form.setFieldsValue({ name: editingItem.name, priceValue: editingItem.priceValue })
      setAppliesOn(editingItem.appliesOn || APPLIES_ON.WEEKEND)
      setCustomDays(editingItem.customDays || [])
      setAffects(editingItem.affects || AFFECTS.ALL)
      setSelectedTickets(editingItem.selectedTickets || [])
      setDirection(editingItem.direction || PRICE_DIRECTION.INCREASE)
      setPriceType(editingItem.priceType || PRICE_TYPE.PERCENT)
    } else if (!open) {
      form.resetFields()
      setAppliesOn(APPLIES_ON.WEEKEND)
      setCustomDays([])
      setAffects(AFFECTS.ALL)
      setSelectedTickets([])
      setDirection(PRICE_DIRECTION.INCREASE)
      setPriceType(PRICE_TYPE.PERCENT)
    }
    setIsTicketSelectionMissing(false)
    setConflictingName(null)
  }, [open, editingItem, form])

  const handleAppliesOnChange = (value) => {
    setAppliesOn(value)
    setConflictingName(null)
  }

  const toggleDay = (day) => {
    setConflictingName(null)
    setCustomDays((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]))
  }

  const toggleTicket = (id) => {
    setIsTicketSelectionMissing(false)
    setConflictingName(null)
    setSelectedTickets((prev) => (prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]))
  }

  const handleAffectsChange = (value) => {
    setAffects(value)
    setIsTicketSelectionMissing(false)
    setConflictingName(null)
  }

  const handleSave = async () => {
    // Checked before validateFields so this error shows alongside field errors, not after them.
    const isMissingTickets = ticketTypes.length > 0 && affects === AFFECTS.SELECTED && selectedTickets.length === 0
    setIsTicketSelectionMissing(isMissingTickets)
    try {
      const values = await form.validateFields()
      if (isMissingTickets) return
      const adjustment = {
        ...(editingItem ? { id: editingItem.id } : {}),
        ...values,
        appliesOn,
        customDays: appliesOn === APPLIES_ON.CUSTOM ? customDays : [],
        affects,
        selectedTickets: affects === AFFECTS.SELECTED ? selectedTickets : [],
        direction,
        priceType,
      }

      // Overlapping adjustments both apply to the same booking and their deltas stack,
      // so a clash is blocked here rather than silently double-charging later.
      const conflict = findConflictingDayAdjustment(adjustment, existingAdjustments)
      if (conflict) {
        setConflictingName(conflict.name || "")
        return
      }

      onSave(adjustment)
    } catch {
      // AntD shows inline field errors
    }
  }

  const footer = (
    <Flex gap={spacings.S} className="mt-m">
      <Button shape="round" color="primary" variant="outlined" size="large" onClick={onCancel}>
        {t("experiences.pricing.adjustment_modal.cancel")}
      </Button>
      <Button shape="round" color="primary" variant="solid" size="large" onClick={handleSave}>
        {t("experiences.pricing.adjustment_modal.save")}
      </Button>
    </Flex>
  )

  const modalTitle = t(
    editingItem
      ? "experiences.pricing.day_adjustment_modal.edit_title"
      : "experiences.pricing.day_adjustment_modal.title"
  )

  const content = (
    <Form
      form={form}
      layout="vertical"
      requiredMark={(label, { required }) => (
        <>
          {label}
          {required && " *"}
        </>
      )}
    >
        <Card className={styles.contentCard}>
          <Flex vertical gap={spacings.XL}>
            <Form.Item
              name="name"
              label={t("experiences.pricing.adjustment_modal.name_label")}
              required
              rules={[{ required: true, message: t("experiences.pricing.adjustment_modal.name_required") }]}
              tooltip={t("experiences.pricing.adjustment_modal.name_tooltip")}
            >
              <Input placeholder={t("experiences.pricing.day_adjustment_modal.name_placeholder")} size="large" />
            </Form.Item>

            <Flex vertical gap={spacings.S}>
              <Text fw={500} size="l">{t("experiences.pricing.adjustment_modal.applies_on")}</Text>
              <Flex vertical gap={spacings.XS}>
                <Radio.Group value={appliesOn} onChange={(e) => handleAppliesOnChange(e.target.value)}>
                  <Flex gap={spacings.XL} wrap>
                    <Radio value={APPLIES_ON.WEEKDAYS}>{t("experiences.pricing.day_adjustment_modal.applies_on_weekdays")}</Radio>
                    <Radio value={APPLIES_ON.WEEKEND}>{t("experiences.pricing.day_adjustment_modal.applies_on_weekend")}</Radio>
                    <Radio value={APPLIES_ON.CUSTOM}>{t("experiences.pricing.day_adjustment_modal.applies_on_custom")}</Radio>
                  </Flex>
                </Radio.Group>

                {appliesOn === APPLIES_ON.CUSTOM && (
                  <Flex gap={spacings.XS} wrap>
                    {WEEKDAYS.map((day) => (
                      <button
                        key={day}
                        type="button"
                        onClick={() => toggleDay(day)}
                        className={`${styles.chip} ${customDays.includes(day) ? styles.chipActive : ""}`}
                      >
                        {day}
                      </button>
                    ))}
                  </Flex>
                )}

                {conflictingName !== null && (
                  <Text size="s" className={styles.errorText}>
                    {t("experiences.pricing.adjustment_modal.duplicate_days", { name: conflictingName })}
                  </Text>
                )}

                <Flex align="center" gap={spacings.XS} className={styles.infoBox}>
                  <InfoCircleOutlined className={styles.infoBoxIcon} />
                  <Text className={styles.infoBoxText}>
                    {t("experiences.pricing.day_adjustment_modal.applies_on_info")}
                  </Text>
                </Flex>
              </Flex>
            </Flex>

            {/* Affects (ticket-type targeting) is only meaningful for ticketed
                experiences; private trips have no ticket types. */}
            {ticketTypes.length > 0 && (
              <>
                <Divider className={styles.divider} />

                <Flex vertical gap={spacings.M}>
                  <Text fw={500} size="l">{t("experiences.pricing.adjustment_modal.affects")}</Text>
                  <Radio.Group value={affects} onChange={(e) => handleAffectsChange(e.target.value)}>
                    <Flex gap={spacings.XL}>
                      <Radio value={AFFECTS.ALL}>{t("experiences.pricing.adjustment_modal.affects_all")}</Radio>
                      <Radio value={AFFECTS.SELECTED}>{t("experiences.pricing.adjustment_modal.affects_selected")}</Radio>
                    </Flex>
                  </Radio.Group>
                  {affects === AFFECTS.SELECTED && (
                    <Flex vertical gap={spacings.XS}>
                      <Flex gap={spacings.XS} wrap>
                        {ticketTypes.map((ticket) => (
                          <button
                            key={ticket.id}
                            type="button"
                            onClick={() => toggleTicket(ticket.id)}
                            className={`${styles.chip} ${selectedTickets.includes(ticket.id) ? styles.chipActive : ""}`}
                          >
                            {ticket.label}
                          </button>
                        ))}
                      </Flex>
                      {isTicketSelectionMissing && (
                        <Text size="s" className={styles.errorText}>{t("experiences.pricing.adjustment_modal.ticket_type_required")}</Text>
                      )}
                    </Flex>
                  )}
                </Flex>
              </>
            )}

            <Divider className={styles.divider} />

            <Flex vertical gap={spacings.M}>
              <Text fw={500} size="l">{t("experiences.pricing.adjustment_modal.price_adjustment")}</Text>
              <Radio.Group value={direction} onChange={(e) => setDirection(e.target.value)}>
                <Flex gap={spacings.XL}>
                  <Radio value={PRICE_DIRECTION.INCREASE}>{t("experiences.pricing.adjustment_modal.increase")}</Radio>
                  <Radio value={PRICE_DIRECTION.DECREASE}>{t("experiences.pricing.adjustment_modal.decrease")}</Radio>
                </Flex>
              </Radio.Group>
              <Flex vertical gap={spacings.XS}>
                <Flex align="center" gap={spacings.XS}>
                  <Form.Item
                    name="priceValue"
                    noStyle
                    rules={[{ required: true, message: t("experiences.pricing.adjustment_modal.value_required") }]}
                  >
                    <InputNumber className={styles.priceInput} size="large" min={0} max={priceType === PRICE_TYPE.PERCENT ? 100 : undefined} placeholder={"0"} />
                  </Form.Item>
                  <Segmented
                    value={priceType}
                    onChange={setPriceType}
                    options={PRICE_TYPE_OPTIONS}
                    size="large"
                    className={styles.segmented}
                  />
                </Flex>
                <Flex align="center" gap={spacings.XS} className={styles.infoBox}>
                  <InfoCircleOutlined className={styles.infoBoxIcon} />
                  <Text className={styles.infoBoxText}>
                    {t("experiences.pricing.adjustment_modal.base_rate_info")}
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </Card>
    </Form>
  )

  if (isMobile) {
    return (
      <CustomDrawer open={open} setOpen={() => onCancel()} title={modalTitle}>
        {content}
        {footer}
      </CustomDrawer>
    )
  }

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={modalTitle}
      width={1040}
      footer={footer}
    >
      {content}
    </Modal>
  )
}

AddDayAdjustmentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  editingItem: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    priceValue: PropTypes.number,
    appliesOn: PropTypes.string,
    customDays: PropTypes.arrayOf(PropTypes.string),
    affects: PropTypes.string,
    selectedTickets: PropTypes.arrayOf(PropTypes.string),
    direction: PropTypes.string,
    priceType: PropTypes.string,
  }),
  existingAdjustments: PropTypes.arrayOf(PropTypes.object),
  ticketTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
}

AddDayAdjustmentModal.defaultProps = {
  editingItem: null,
  ticketTypes: PLACEHOLDER_TICKET_TYPES,
}

export default AddDayAdjustmentModal
